import { BitkitCloseButton } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Card } from '@chakra-ui/react/card';
import { Text } from '@chakra-ui/react/text';
import { useContext } from 'react';

import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import AddToQuarantineButton from '@/pages/App/TestQuarantinePage/components/AddToQuarantineButton';

import { Attachment, TestCaseRun, TestStatus } from '../TestResults.types';
import Breadcrumb from './components/Breadcrumb';
import DividedInfoItems from './components/DividedInfoItems';
import Duration from './components/Duration';
import FlakyBadge from './components/FlakyBadge';
import StatusBadge from './components/StatusBadge';
import TestCaseRuns from './components/TestCaseRuns';
import WorkflowLink from './components/WorkflowLink';
import TestCaseDetailsTabs from './tabs/TestCaseDetailsTabs';

type TestCaseDetailsProps = {
  runs?: TestCaseRun[];
  name?: string;
  status?: TestStatus;
  message?: string;
  isFlaky?: boolean;
  breadcrumb?: string;
  attachments?: Attachment[];
  durationInMs?: number;
  workflowName?: string;
  workflowLinkUrl?: string;
  testCaseClassName?: string;
  testCaseIdentifier?: string;
  onClose?: () => void;
  testSuites?: string[];
  projectSlug: string;
  runnableType: 'build' | 'pipeline';
  runnableSlug: string;
  runnableNumber: number;
};

const TestCaseDetails = ({
  runs,
  name,
  status,
  isFlaky,
  message,
  breadcrumb,
  attachments,
  durationInMs,
  workflowName,
  workflowLinkUrl,
  testCaseClassName,
  testCaseIdentifier,
  onClose,
  testSuites,
  projectSlug,
  runnableType,
  runnableSlug,
  runnableNumber,
}: TestCaseDetailsProps) => {
  const { account } = useContext(GlobalPropsContext);

  return (
    <Card.Root position="relative" minH="100%" bg="background/secondary">
      <BitkitCloseButton
        size="md"
        position="absolute"
        insetBlockStart="12"
        insetInlineEnd="12"
        colorVariant="neutral"
        onClick={onClose}
      />

      <Box
        p="16"
        gap="8"
        display="flex"
        flexDir="column"
        overflow="hidden"
        bg="background/primary"
        borderTopRadius="8"
        borderBottom="1px solid"
        borderColor="border/minimal"
      >
        <Breadcrumb breadcrumb={breadcrumb} testCaseClassName={testCaseClassName} />

        {name && <Text textStyle="body/lg/semibold">{name}</Text>}

        <DividedInfoItems skip={[FlakyBadge, AddToQuarantineButton]}>
          {status && <StatusBadge status={status} />}
          {isFlaky && <FlakyBadge />}
          {durationInMs !== undefined && <Duration durationInMs={durationInMs} />}
          {workflowName && <WorkflowLink workflowName={workflowName} workflowLinkUrl={workflowLinkUrl} />}
          {isFlaky && (
            <AddToQuarantineButton
              buildNumber={runnableNumber}
              buildSlug={runnableSlug}
              canAccessTestQuarantine={!!account?.canAccessTestQuarantine}
              className={testCaseClassName}
              projectSlug={projectSlug}
              source={runnableType}
              testCaseName={name}
              testCaseIdentifier={testCaseIdentifier}
              testSuiteName={testSuites}
              workflowName={workflowName}
            />
          )}
        </DividedInfoItems>
      </Box>

      {runs && runs.length > 0 ? (
        <TestCaseRuns runs={runs} />
      ) : (
        <TestCaseDetailsTabs key={[breadcrumb, name].join('-')} testOutput={message} attachments={attachments} />
      )}
    </Card.Root>
  );
};

export default TestCaseDetails;
